import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Globe } from "lucide-react";

export default function Navbar({ lang, setLang, t }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const links = [
    { href: "#about", label: t.nav.about },
    { href: "#gallery", label: t.nav.gallery },
    { href: "#locations", label: t.nav.locations },
    { href: "#reviews", label: t.nav.reviews },
    { href: "#contact", label: t.nav.contact },
  ];

  const toggleLang = () => setLang(lang === "es" ? "en" : "es");

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-bn-bg/95 backdrop-blur-md border-b border-bn-border shadow-sm"
          : "bg-gradient-to-b from-black/60 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <a href="#home" data-testid="navbar-logo" className="flex items-center gap-2">
          <div className="bg-bn-primary text-bn-bg px-3 py-1.5 font-display text-lg md:text-xl tracking-wide leading-none">
            BODEGÓN
          </div>
          <div
            className={`hidden sm:block font-display text-lg md:text-xl leading-none transition-colors ${
              scrolled ? "text-bn-secondary" : "text-bn-bg"
            }`}
          >
            NÚÑEZ
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className={`font-body text-sm font-medium hover:text-bn-accent transition-colors uppercase tracking-wider ${
                scrolled ? "text-bn-text" : "text-bn-bg"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle-button"
            onClick={toggleLang}
            className={`hidden md:flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider px-2.5 py-1 border rounded-sm transition-colors ${
              scrolled
                ? "text-bn-text border-bn-border hover:border-bn-primary hover:text-bn-primary"
                : "text-bn-bg border-bn-bg/40 hover:bg-bn-bg/10 hover:border-bn-bg"
            }`}
          >
            <Globe size={14} />
            {lang === "es" ? "EN" : "ES"}
          </button>

          <a
            href="#contact"
            data-testid="navbar-reserve-button"
            className="hidden sm:inline-flex items-center gap-2 bg-bn-primary text-bn-bg px-4 md:px-5 py-2.5 font-body font-semibold text-sm uppercase tracking-wider hover:bg-bn-primary-dark transition-colors"
          >
            {t.nav.reserve}
            <ArrowRight size={16} />
          </a>

          <button
            onClick={() => setOpen(!open)}
            className={`lg:hidden transition-colors ${scrolled ? "text-bn-secondary" : "text-bn-bg"}`}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-bn-bg border-t border-bn-border overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-5 py-4 gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="py-3 font-body font-medium text-bn-text hover:text-bn-primary uppercase tracking-wider text-sm border-b border-bn-border last:border-0"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="mobile-lang-toggle"
                onClick={toggleLang}
                className="mt-3 flex items-center justify-center gap-2 py-3 border border-bn-border text-bn-text font-medium text-sm uppercase tracking-wider"
              >
                <Globe size={16} /> {lang === "es" ? "English" : "Español"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
